/**
 * Custom hook: Settings-page actions
 *  - Job positions (CRUD)
 *  - Clothing types (CRUD)
 *  - Glass types (CRUD)
 *  - Backup / restore / data cleanup / full reset
 */

import axios from 'axios';
import { API } from '../utils/constants';
import { todayISO } from '../utils/helpers';

export const useSettingsActions = ({
  user,
  location,
  locationName,
  // Dictionary form state
  newJobPosition,
  setNewJobPosition,
  editJobPositionName,
  setEditJobPositionName,
  setEditingJobPositionId,
  newClothingType,
  setNewClothingType,
  editClothingTypeName,
  setEditClothingTypeName,
  setEditingClothingTypeId,
  // Glass types
  newGlassType,
  setNewGlassType,
  glassTypes,
  setGlassTypes,
  editGlassTypeName,
  setEditGlassTypeName,
  setEditingGlassTypeId,
  // Cleanup
  daysToKeep,
  closedDays,
  // Backup UI state
  setShowBackupReminder,
  setLastBackupDate,
  // Refreshers
  refresh,
  fetchBackupData,
}) => {
  // ---------- Job positions ----------
  const handleAddJobPosition = async () => {
    if (!newJobPosition.trim()) {
      alert('Wpisz nazwę stanowiska.');
      return;
    }
    try {
      await axios.post(`${API}/job-positions`, { name: newJobPosition.trim(), location });
      setNewJobPosition('');
      refresh();
      alert('Stanowisko dodane!');
    } catch (e) {
      alert(e.response?.data?.detail || 'Błąd.');
    }
  };

  const handleDeleteJobPosition = async (positionId) => {
    if (!await window.confirm('Usunąć to stanowisko?')) return;
    try {
      await axios.delete(`${API}/job-positions/${positionId}`);
      refresh();
    } catch (e) {
      alert(e.response?.data?.detail || 'Błąd.');
    }
  };

  const handleUpdateJobPosition = async (positionId) => {
    if (!editJobPositionName.trim()) {
      alert('Nazwa stanowiska nie może być pusta');
      return;
    }
    try {
      const res = await fetch(`${API}/job-positions/${positionId}?name=${encodeURIComponent(editJobPositionName)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Błąd podczas aktualizacji');
      }
      alert('Stanowisko zaktualizowane!');
      setEditingJobPositionId(null);
      setEditJobPositionName('');
      refresh();
    } catch (err) {
      alert(err.message);
    }
  };

  // ---------- Clothing types ----------
  const handleAddClothingType = async () => {
    if (!newClothingType.trim()) {
      alert('Wpisz nazwę typu odzieży.');
      return;
    }
    try {
      await axios.post(`${API}/clothing-types`, { name: newClothingType.trim(), location });
      setNewClothingType('');
      refresh();
      alert('Typ odzieży dodany!');
    } catch (e) {
      alert(e.response?.data?.detail || 'Błąd.');
    }
  };

  const handleDeleteClothingType = async (typeId) => {
    if (!await window.confirm('Usunąć ten typ odzieży?')) return;
    try {
      await axios.delete(`${API}/clothing-types/${typeId}`);
      refresh();
    } catch (e) {
      alert(e.response?.data?.detail || 'Błąd.');
    }
  };

  const handleUpdateClothingType = async (typeId) => {
    if (!editClothingTypeName.trim()) {
      alert('Nazwa typu odzieży nie może być pusta');
      return;
    }
    try {
      const res = await fetch(`${API}/clothing-types/${typeId}?name=${encodeURIComponent(editClothingTypeName)}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.detail || 'Błąd podczas aktualizacji');
      }
      alert('Typ odzieży zaktualizowany!');
      setEditingClothingTypeId(null);
      setEditClothingTypeName('');
      refresh();
    } catch (err) {
      alert(err.message);
    }
  };

  // ---------- Glass types ----------
  const handleAddGlassType = async () => {
    if (!newGlassType.trim()) {
      alert('Podaj nazwę rodzaju szkła');
      return;
    }
    try {
      const response = await axios.post(`${API}/glass/types`, { name: newGlassType, location });
      if (response.status === 200) {
        setGlassTypes([...glassTypes, response.data]);
        setNewGlassType('');
        alert('✅ Dodano rodzaj szkła!');
      }
    } catch (e) {
      alert(e.response?.data?.detail || 'Błąd dodawania rodzaju szkła');
    }
  };

  const handleUpdateGlassType = async (typeId) => {
    if (!editGlassTypeName.trim()) {
      alert('Podaj nazwę rodzaju szkła');
      return;
    }
    try {
      const response = await axios.put(`${API}/glass/types/${typeId}`, { name: editGlassTypeName, location });
      if (response.status === 200) {
        setGlassTypes(glassTypes.map((t) => (t.id === typeId ? { ...t, name: editGlassTypeName } : t)));
        setEditingGlassTypeId(null);
        setEditGlassTypeName('');
        alert('✅ Zaktualizowano rodzaj szkła!');
      }
    } catch (e) {
      alert(e.response?.data?.detail || 'Błąd aktualizacji rodzaju szkła');
    }
  };

  const handleDeleteGlassType = async (typeId) => {
    if (!await window.confirm('Usunąć ten rodzaj szkła?')) return;
    try {
      await axios.delete(`${API}/glass/types/${typeId}`);
      setGlassTypes(glassTypes.filter((t) => t.id !== typeId));
      alert('✅ Usunięto rodzaj szkła');
    } catch (e) {
      alert(e.response?.data?.detail || 'Błąd usuwania rodzaju szkła');
    }
  };

  // ---------- Backup / restore ----------
  const handleConfirmBackup = async () => {
    try {
      await axios.post(`${API}/settings/confirm-backup`, { location, confirmedBy: user.username });
      setShowBackupReminder(false);
      setLastBackupDate(new Date().toISOString());
      alert('✓ Backup potwierdzony! Przypomnienie za tydzień.');
    } catch (e) {
      alert(e.response?.data?.detail || 'Błąd.');
    }
  };

  const handleBackupDownload = async () => {
    try {
      const res = await axios.get(`${API}/settings/backup?location=${location}`);
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `mrjerry-backup-${locationName.replace(/\s/g, '_')}-${todayISO()}.json`;
      a.click();
      fetchBackupData();
    } catch (e) {
      alert(`Błąd backup: ${e.response?.data?.detail || e.message || 'Nieznany błąd'}`);
    }
  };

  const handleRestoreFromArchive = async (backupId, backupDate) => {
    if (!await window.confirm(`Przywrócić dane z backupu ${backupDate}?\n\nTo nadpisze obecne dane!`)) return;
    try {
      await axios.post(`${API}/settings/backups/${backupId}/restore`);
      alert('✅ Dane przywrócone z archiwum!');
      refresh();
    } catch (e) {
      alert('Błąd podczas przywracania: ' + (e.response?.data?.detail || 'Nieznany błąd'));
    }
  };

  const handleDeleteArchivedBackup = async (backupId, backupDate) => {
    if (!await window.confirm(`Usunąć backup z ${backupDate}?`)) return;
    try {
      await axios.delete(`${API}/settings/backups/${backupId}`);
      alert('✅ Backup usunięty');
      fetchBackupData();
    } catch (e) {
      alert('Błąd podczas usuwania');
    }
  };

  const handleRestoreFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const parsed = JSON.parse(String(reader.result));
        const incoming =
          parsed?.app === 'mrjerry_tips_registry' && parsed?.data
            ? parsed
            : { app: 'mrjerry_tips_registry', version: 1, data: parsed };
        if (!await window.confirm(`Przywrócić dane dla ${locationName}? To nadpisze obecne dane tej lokalizacji.`)) return;
        await axios.post(`${API}/settings/restore?location=${location}`, incoming);
        alert('Przywrócono!');
        refresh();
      } catch {
        alert('Błąd wczytywania.');
      }
    };
    reader.readAsText(file);
    e.target.value = '';
  };

  const handleResetAll = async () => {
    const locationNameUpper = locationName.toUpperCase();
    const userInput = await window.prompt(
      `⚠️ OSTRZEŻENIE! Ta operacja TRWALE usunie wszystkie dane dla lokacji ${locationName}.\n\n` +
        `Zostaną usunięte:\n` +
        `• Wszystkie wpisy napiwków\n` +
        `• Wszyscy pracownicy\n` +
        `• Wszystkie wpisy billard\n` +
        `• Cała odzież firmowa\n` +
        `• Zamknięte dni\n\n` +
        `Aby potwierdzić, wpisz nazwę lokacji: ${locationNameUpper}`
    );
    if (!userInput) return;
    if (userInput.toUpperCase() !== locationNameUpper) {
      alert(`❌ Nieprawidłowa nazwa lokacji. Wpisano: "${userInput}"\nOczekiwano: "${locationNameUpper}"`);
      return;
    }
    if (!await window.confirm(`OSTATNIE POTWIERDZENIE: Czy na pewno usunąć WSZYSTKIE dane dla ${locationName}?`)) return;
    try {
      await axios.delete(`${API}/reset?location=${location}`);
      alert('✅ Wszystkie dane zostały usunięte.');
      refresh();
    } catch (e) {
      alert('Błąd podczas usuwania danych.');
    }
  };

  const handleCleanupData = async () => {
    const days = parseInt(daysToKeep, 10) || 30;
    const conf = await window.confirm(
      `Czy na pewno usunąć dane starsze niż ${days} dni?\n\n` +
        `Zostaną usunięte:\n` +
        `- Wpisy napiwków\n` +
        `- Zamknięte dni\n` +
        `- Wpisy bilarda\n\n` +
        `ZACHOWANE:\n` +
        `- Odzież firmowa (wszystkie dane)\n` +
        `- Pracownicy\n` +
        `- Użytkownicy\n\n` +
        `Operacja nieodwracalna!`
    );
    if (!conf) return;
    try {
      const result = await axios.post(`${API}/cleanup-old-data?location=${location}&days_to_keep=${days}`);
      refresh();
      const deleted = result.data.deleted;
      alert(
        `Dane wyczyszczone!\n\n` +
          `Usunięto:\n` +
          `- Wpisy napiwków: ${deleted.entries}\n` +
          `- Zamknięte dni: ${deleted.closed_days}\n` +
          `- Billard: ${deleted.billard}\n\n` +
          `Data graniczna: ${result.data.cutoff_date}\n\n` +
          `✅ Odzież firmowa została zachowana!`
      );
    } catch (e) {
      alert(e.response?.data?.detail || 'Błąd czyszczenia danych.');
    }
  };

  const handleClearOldExports = async () => {
    const cutoffDays = prompt('Usuń dni starsze niż ile dni? (np. 90)', '90');
    if (!cutoffDays || isNaN(cutoffDays)) return;
    const cutoffDate = new Date();
    cutoffDate.setDate(cutoffDate.getDate() - parseInt(cutoffDays, 10));
    const cutoffStr = cutoffDate.toISOString().split('T')[0];
    const oldDays = Object.keys(closedDays).filter((date) => date < cutoffStr);
    if (oldDays.length === 0) {
      alert(`Brak dni starszych niż ${cutoffDays} dni.`);
      return;
    }
    if (!await window.confirm(`Usunąć ${oldDays.length} dni starszych niż ${cutoffDays} dni?\n\nUWAGA: To usunie dni z bazy danych!`)) return;
    try {
      for (const date of oldDays) {
        await axios.delete(`${API}/closed-days/${date}?location=${location}`);
      }
      alert(`Usunięto ${oldDays.length} dni.`);
      refresh();
    } catch (e) {
      alert('Błąd podczas usuwania: ' + (e.response?.data?.detail || e.message));
    }
  };

  return {
    // job positions
    handleAddJobPosition,
    handleDeleteJobPosition,
    handleUpdateJobPosition,
    // clothing types
    handleAddClothingType,
    handleDeleteClothingType,
    handleUpdateClothingType,
    // glass types
    handleAddGlassType,
    handleUpdateGlassType,
    handleDeleteGlassType,
    // backup / restore / cleanup
    handleConfirmBackup,
    handleBackupDownload,
    handleRestoreFromArchive,
    handleDeleteArchivedBackup,
    handleRestoreFile,
    handleResetAll,
    handleCleanupData,
    handleClearOldExports,
  };
};
